import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@cuspar/ui';
import { ArrowRight, Check, ClipboardCopy, Copy, Download, Globe, MessageSquare, Sparkles, Upload } from 'lucide-react';
import { YWT_PUBLIC_CONFIG } from '@cuspar/ywt.ch.config/public';
import { ROUTES } from '../../routes';
import { authEntryRoute, useAuthStore } from '../../state/authStore';

/** The docs page the AI assistant reads, and the same starter prompt as plain text for assistants with browsing off. */
export const AI_DOCS_URL = `${YWT_PUBLIC_CONFIG.docsUrl}/ai`;
export const AI_PROMPT_URL = `${AI_DOCS_URL}/prompt.txt`;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // No clipboard access (insecure context, older browser): the caller tells the customer to copy by hand.
    return false;
  }
}

/**
 * Public page for the unconnected path: the customer copies one line into the
 * ChatGPT or Claude they already have, the assistant builds one `index.html`,
 * and they upload it on the site's Edit tab.
 */
export const AiAssistantPage = () => {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const oneLiner = t('aiAssistant.oneLiner', { url: AI_DOCS_URL });
  const [oneLinerCopied, setOneLinerCopied] = useState(false);
  const [fetchingPrompt, setFetchingPrompt] = useState(false);

  async function copyOneLiner() {
    if (await copyText(oneLiner)) {
      setOneLinerCopied(true);
      toast.success(t('aiAssistant.copied'));
      setTimeout(() => setOneLinerCopied(false), 2000);
    } else {
      toast.error(t('aiAssistant.copyFailed'));
    }
  }

  // The full prompt lives on the docs site; when it cannot be fetched or copied, the docs page itself is the fallback.
  async function copyFullPrompt() {
    setFetchingPrompt(true);
    try {
      const response = await fetch(AI_PROMPT_URL);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      if (!(await copyText(text))) throw new Error('clipboard');
      toast.success(t('aiAssistant.fullPromptCopied'));
    } catch {
      toast.error(t('aiAssistant.fullPromptFailed'));
      window.open(AI_DOCS_URL, '_blank', 'noopener,noreferrer');
    } finally {
      setFetchingPrompt(false);
    }
  }

  return (
    <div className='min-h-screen bg-background font-mono text-foreground'>
      <section className='container mx-auto px-4 max-w-3xl text-center'>
        <div className='mx-auto mb-6 flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary'>
          <Sparkles className='size-7' />
        </div>
        <h1 className='font-mono text-2xl md:text-4xl font-bold leading-tight tracking-tighter lg:leading-[1.1] mb-4'>{t('aiAssistant.title')}</h1>
        <p className='text-sm md:text-base text-muted-foreground'>{t('aiAssistant.pitch')}</p>
      </section>

      {/* Three steps */}
      <section className='container mx-auto px-4 py-12 md:py-16 max-w-3xl space-y-6'>
        <Step number={1} icon={<MessageSquare className='w-5 h-5' />} title={t('aiAssistant.step1Title')}>
          <div className='flex flex-col sm:flex-row gap-2 sm:items-stretch'>
            <div className='flex-1 rounded-md border bg-muted/50 px-3 py-2 text-sm break-words select-all' data-testid='one-liner'>
              {oneLiner}
            </div>
            <Button variant='outline' onClick={copyOneLiner} className='shrink-0'>
              {oneLinerCopied ? <Check className='w-4 h-4 mr-2 text-green-600' /> : <Copy className='w-4 h-4 mr-2' />}
              {oneLinerCopied ? t('aiAssistant.copiedShort') : t('aiAssistant.copy')}
            </Button>
          </div>
          <p className='text-xs text-muted-foreground'>{t('aiAssistant.step1Description')}</p>
        </Step>

        <Step number={2} icon={<Download className='w-5 h-5' />} title={t('aiAssistant.step2Title')}>
          <p className='text-sm text-muted-foreground'>{t('aiAssistant.step2Description')}</p>
        </Step>

        <Step number={3} icon={<Upload className='w-5 h-5' />} title={t('aiAssistant.step3Title')}>
          <p className='text-sm text-muted-foreground'>{t('aiAssistant.step3Description')}</p>
          <div className='flex flex-col sm:flex-row gap-2 sm:items-center'>
            <Link to={user ? ROUTES.account.websites : authEntryRoute()} state={user ? undefined : { from: ROUTES.account.websites }}>
              <Button className='w-full sm:w-auto'>
                <Globe className='w-4 h-4 mr-2' />
                {t('aiAssistant.goToHosting')}
                <ArrowRight className='w-4 h-4 ml-2' />
              </Button>
            </Link>
            <Link to={ROUTES.hosting.static} className='text-sm underline underline-offset-4 text-muted-foreground hover:text-foreground sm:ml-2'>
              {t('aiAssistant.seePlans')}
            </Link>
          </div>
        </Step>
      </section>

      {/* Where to paste it: one tab per assistant */}
      <section className='container mx-auto px-4 pb-12 md:pb-16 max-w-3xl'>
        <h2 className='text-lg md:text-xl font-bold mb-4'>{t('aiAssistant.tabsTitle')}</h2>
        <Tabs defaultValue='chatgpt'>
          <TabsList>
            <TabsTrigger value='chatgpt'>ChatGPT</TabsTrigger>
            <TabsTrigger value='claude'>Claude</TabsTrigger>
          </TabsList>
          <TabsContent value='chatgpt'>
            <ClickSteps steps={[t('aiAssistant.chatgptOpen'), t('aiAssistant.pasteAndEnter'), t('aiAssistant.noBrowsingHint'), t('aiAssistant.chatgptResult')]} />
          </TabsContent>
          <TabsContent value='claude'>
            <ClickSteps steps={[t('aiAssistant.claudeOpen'), t('aiAssistant.pasteAndEnter'), t('aiAssistant.noBrowsingHint'), t('aiAssistant.claudeResult')]} />
          </TabsContent>
        </Tabs>

        <div className='mt-6 rounded-xl border bg-muted/30 p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4'>
          <div>
            <h3 className='font-semibold text-sm md:text-base mb-1'>{t('aiAssistant.fullPromptTitle')}</h3>
            <p className='text-xs md:text-sm text-muted-foreground'>{t('aiAssistant.fullPromptDescription')}</p>
          </div>
          <div className='flex flex-col sm:flex-row gap-2 shrink-0'>
            <Button variant='secondary' onClick={copyFullPrompt} loading={fetchingPrompt}>
              <ClipboardCopy className='w-4 h-4 mr-2' />
              {t('aiAssistant.copyFullPrompt')}
            </Button>
            <a href={AI_DOCS_URL} target='_blank' rel='noreferrer' className='text-xs text-muted-foreground underline underline-offset-4 self-center'>
              {t('aiAssistant.openDocs')}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className='container max-w-3xl mx-auto pb-12 px-4'>
        <h2 className='text-lg font-medium mb-4 text-center text-muted-foreground'>{t('common.faq')}</h2>
        <Accordion type='single' collapsible className='w-full'>
          <AccordionItem value='photos'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('aiAssistant.faqPhotosQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('aiAssistant.faqPhotosA')}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value='changes'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('aiAssistant.faqChangesQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('aiAssistant.faqChangesA')}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value='cost'>
            <AccordionTrigger className='py-2 text-sm text-muted-foreground'>{t('aiAssistant.faqCostQ')}</AccordionTrigger>
            <AccordionContent forceMount className='text-sm pb-2'>
              {t('aiAssistant.faqCostA')}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>
    </div>
  );
};

function Step({ number, icon, title, children }: { number: number; icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className='rounded-xl border bg-card p-5 md:p-6 shadow-sm flex gap-4'>
      <div className='shrink-0 flex flex-col items-center gap-2'>
        <div className='flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold'>{number}</div>
        <div className='text-primary'>{icon}</div>
      </div>
      <div className='flex-1 min-w-0 space-y-3'>
        <h2 className='font-semibold text-base md:text-lg leading-snug'>{title}</h2>
        {children}
      </div>
    </div>
  );
}

function ClickSteps({ steps }: { steps: string[] }) {
  return (
    <ol className='mt-3 space-y-2 text-sm'>
      {steps.map((step, index) => (
        <li key={index} className='flex gap-3'>
          <span className='shrink-0 text-muted-foreground tabular-nums'>{index + 1}.</span>
          <span>{step}</span>
        </li>
      ))}
    </ol>
  );
}
